import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@layout/Layout';
import { Calendar, Sparkles, CalendarDays, TrendingUp, Users, CheckCircle2, Clock, ChevronRight, AlertCircle, ChevronLeft } from 'lucide-react';
import { useTheme } from '@contexts/ThemeContext';
import useHolidayStore from '@/store/HolidayStore';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Tabs,
    Tab,
    CircularProgress,
    Chip,
    Stack,
    Button,
    useTheme as useMuiTheme,
    alpha,
    IconButton,
    Tooltip
} from '@mui/material';

const HolidayCalendarView: React.FC = () => {
    const { isDarkMode } = useTheme();
    const theme = useMuiTheme();
    const navigate = useNavigate();

    const {
        publicHolidays,
        optionalHolidays,
        selectedOptionalHolidays: storeSelectedHolidays,
        loading,
        error,
        maxOptionalSelections,
        fetchPublicHolidays,
        fetchOptionalHolidays,
        fetchSelectedOptionalHolidays,
        fetchHolidayConfig,
        checkYearData
    } = useHolidayStore();

    const [activeTab, setActiveTab] = useState<'all' | 'public' | 'optional'>('all');
    const [currentFyYear, setCurrentFyYear] = useState<number>(() => {
        const today = new Date();
        const currentMonth = today.getMonth();
        return currentMonth >= 3 ? today.getFullYear() : today.getFullYear() - 1;
    });
    const [hasPrevYearData, setHasPrevYearData] = useState(false);
    const [hasNextYearData, setHasNextYearData] = useState(false);

    // Get financial year string from year number
    const getFinancialYearString = (fyYear: number) => {
        return `${fyYear}-${fyYear + 1}`;
    };

    const financialYear = getFinancialYearString(currentFyYear);

    // Check if adjacent years have data
    const checkAdjacentYears = async () => {
        const [prevHasData, nextHasData] = await Promise.all([
            checkYearData(currentFyYear - 1),
            checkYearData(currentFyYear + 1)
        ]);
        setHasPrevYearData(prevHasData);
        setHasNextYearData(nextHasData);
    };

    useEffect(() => {
        const loadHolidays = async () => {
            try {
                console.log(`[HolidayCalendarView] Loading holidays for FY Year: ${currentFyYear}`);
                console.log(`[HolidayCalendarView] Current maxOptionalSelections BEFORE fetch: ${maxOptionalSelections}`);

                await Promise.all([
                    fetchPublicHolidays(currentFyYear),
                    fetchOptionalHolidays(currentFyYear),
                    fetchSelectedOptionalHolidays(),
                    fetchHolidayConfig(currentFyYear)
                ]);

                await checkAdjacentYears();

                // Wait a bit for state to update, then log
                setTimeout(() => {
                    console.log(`[HolidayCalendarView] Current maxOptionalSelections AFTER fetch: ${maxOptionalSelections}`);
                }, 100);
            } catch (error) {
                console.error('Failed to load holidays:', error);
            }
        };

        loadHolidays();
    }, [currentFyYear]);

    const handlePreviousYear = async () => {
        if (hasPrevYearData) {
            setCurrentFyYear(currentFyYear - 1);
        }
    };

    const handleNextYear = async () => {
        if (hasNextYearData) {
            setCurrentFyYear(currentFyYear + 1);
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const getDayName = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { weekday: 'short' });
    };

    const getMonthName = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'short' });
    };

    const getDay = (dateString: string) => {
        const date = new Date(dateString);
        return date.getDate();
    };

    const getDaysUntil = (dateString: string) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const targetDate = new Date(dateString);
        targetDate.setHours(0, 0, 0, 0);
        const diffTime = targetDate.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    const isUpcoming = (dateString: string) => {
        return getDaysUntil(dateString) >= 0;
    };

    // Filter selected optional holidays to only show those in the current FY year being viewed
    const getFyYearDateRange = (fyYear: number) => {
        const startDate = `${fyYear}-04-01`;
        const endDate = `${fyYear + 1}-03-31`;
        return { startDate, endDate };
    };

    const { startDate: fyStartDate, endDate: fyEndDate } = getFyYearDateRange(currentFyYear);

    const selectedOptionalHolidaysData = (storeSelectedHolidays || []).filter((holiday: any) => {
        const holidayDate = holiday.holiday_date;
        return holidayDate >= fyStartDate && holidayDate <= fyEndDate;
    });

    // Get next upcoming holiday
    const getNextUpcomingHoliday = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const relevantHolidays = [...(publicHolidays || []), ...selectedOptionalHolidaysData].sort((a: any, b: any) =>
            new Date(a.holiday_date).getTime() - new Date(b.holiday_date).getTime()
        );

        return relevantHolidays.find((holiday: any) => new Date(holiday.holiday_date) >= today);
    };

    const upcomingHoliday = getNextUpcomingHoliday();

    // Combine and sort all holidays by date
    const getAllHolidaysSorted = () => {
        const allHolidays = [
            ...(publicHolidays || []).map((h: any) => ({ ...h, type: 'public' })),
            ...selectedOptionalHolidaysData.map((h: any) => ({ ...h, type: 'optional' }))
        ];

        return allHolidays.sort((a: any, b: any) =>
            new Date(a.holiday_date).getTime() - new Date(b.holiday_date).getTime()
        );
    };

    const allHolidaysSorted = getAllHolidaysSorted();

    // Filter holidays based on active tab
    const getFilteredHolidays = () => {
        if (activeTab === 'all') return allHolidaysSorted;
        if (activeTab === 'public') return allHolidaysSorted.filter((h: any) => h.type === 'public');
        return allHolidaysSorted.filter((h: any) => h.type === 'optional');
    };

    const filteredHolidays = getFilteredHolidays();

    // Statistics
    const totalHolidays = allHolidaysSorted.length;
    const publicHolidaysCount = publicHolidays?.length || 0;
    const optionalHolidaysCount = selectedOptionalHolidaysData.length;
    const upcomingHolidaysCount = allHolidaysSorted.filter((h: any) => isUpcoming(h.holiday_date)).length;

    // Debug log to see current maxOptionalSelections
    console.log(`[HolidayCalendarView RENDER] FY Year: ${currentFyYear}, maxOptionalSelections: ${maxOptionalSelections}, optionalHolidaysCount: ${optionalHolidaysCount}`);

    return (
        <Layout>
            <Box sx={{ p: 3, minHeight: '100vh', bgcolor: 'background.default' }}>
                {/* Header Section */}
                <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 1 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Stack spacing={3}>
                            {/* Title with Navigation */}
                            <Box>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        <Calendar size={32} color={theme.palette.primary.main} />
                                        <Typography variant="h4" fontWeight={600} color="text.primary">
                                            Holiday Calendar {financialYear}
                                        </Typography>
                                    </Stack>

                                    {/* Year Navigation Buttons */}
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Tooltip title={hasPrevYearData ? `Go to FY ${currentFyYear - 1}-${currentFyYear}` : 'No data for previous year'}>
                                            <span>
                                                <IconButton
                                                    onClick={handlePreviousYear}
                                                    disabled={!hasPrevYearData || loading}
                                                    sx={{
                                                        bgcolor: hasPrevYearData ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.action.disabled, 0.05),
                                                        '&:hover': {
                                                            bgcolor: hasPrevYearData ? alpha(theme.palette.primary.main, 0.2) : alpha(theme.palette.action.disabled, 0.05)
                                                        },
                                                        '&:disabled': {
                                                            opacity: 0.4
                                                        }
                                                    }}
                                                >
                                                    <ChevronLeft size={20} color={hasPrevYearData ? theme.palette.primary.main : theme.palette.action.disabled} />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Chip
                                            label={`FY ${financialYear}`}
                                            color="primary"
                                            sx={{ fontWeight: 600, fontSize: '0.875rem', px: 1 }}
                                        />
                                        <Tooltip title={hasNextYearData ? `Go to FY ${currentFyYear + 1}-${currentFyYear + 2}` : 'No data for next year'}>
                                            <span>
                                                <IconButton
                                                    onClick={handleNextYear}
                                                    disabled={!hasNextYearData || loading}
                                                    sx={{
                                                        bgcolor: hasNextYearData ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.action.disabled, 0.05),
                                                        '&:hover': {
                                                            bgcolor: hasNextYearData ? alpha(theme.palette.primary.main, 0.2) : alpha(theme.palette.action.disabled, 0.05)
                                                        },
                                                        '&:disabled': {
                                                            opacity: 0.4
                                                        }
                                                    }}
                                                >
                                                    <ChevronRight size={20} color={hasNextYearData ? theme.palette.primary.main : theme.palette.action.disabled} />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Stack>
                                </Stack>
                                <Typography variant="body2" color="text.secondary">
                                    View and manage all company holidays for the financial year
                                </Typography>
                            </Box>

                            {/* Stats Cards */}
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <Card sx={{ flex: 1, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: 'white' }}>
                                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                                        <Box sx={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            bgcolor: alpha('#fff', 0.2)
                                        }}>
                                            <CalendarDays size={24} />
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" sx={{ opacity: 0.9, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                                Total Holidays
                                            </Typography>
                                            <Typography variant="h4" fontWeight={600}>{totalHolidays}</Typography>
                                        </Box>
                                    </CardContent>
                                </Card>

                                <Card sx={{ flex: 1, background: 'linear-gradient(135deg, #f59e0b, #ef4444)', color: 'white' }}>
                                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                                        <Box sx={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            bgcolor: alpha('#fff', 0.2)
                                        }}>
                                            <Users size={24} />
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" sx={{ opacity: 0.9, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                                Public Holidays
                                            </Typography>
                                            <Typography variant="h4" fontWeight={600}>{publicHolidaysCount}</Typography>
                                        </Box>
                                    </CardContent>
                                </Card>

                                <Card sx={{ flex: 1, background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white' }}>
                                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                                        <Box sx={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            bgcolor: alpha('#fff', 0.2)
                                        }}>
                                            <Sparkles size={24} />
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" sx={{ opacity: 0.9, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                                Your Optional
                                            </Typography>
                                            <Typography variant="h4" fontWeight={600}>{optionalHolidaysCount}/{maxOptionalSelections}</Typography>
                                        </Box>
                                    </CardContent>
                                </Card>

                                <Card sx={{ flex: 1, background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: 'white' }}>
                                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                                        <Box sx={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            bgcolor: alpha('#fff', 0.2)
                                        }}>
                                            <TrendingUp size={24} />
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" sx={{ opacity: 0.9, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                                Upcoming
                                            </Typography>
                                            <Typography variant="h4" fontWeight={600}>{upcomingHolidaysCount}</Typography>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Stack>
                        </Stack>
                    </CardContent>
                </Card>

                {/* No Optional Holidays Selected Notice */}
                {optionalHolidaysCount === 0 && (
                    <Card sx={{
                        mb: 3,
                        borderRadius: 2,
                        bgcolor: isDarkMode ? alpha(theme.palette.warning.dark, 0.15) : alpha(theme.palette.warning.light, 0.15),
                        border: 2,
                        borderColor: 'warning.main',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.warning.main, 0.2)}`
                    }}>
                        <CardContent sx={{ p: 3 }}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                                <Box sx={{
                                    width: 56,
                                    height: 56,
                                    borderRadius: '50%',
                                    bgcolor: alpha(theme.palette.warning.main, 0.2),
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0
                                }}>
                                    <AlertCircle size={32} color={theme.palette.warning.main} />
                                </Box>
                                <Stack flex={1} spacing={0.5}>
                                    <Typography variant="h6" fontWeight={600} color="warning.main">
                                        No Optional Holidays Selected
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        You haven't selected your optional holidays yet. Visit the Holiday Selection page to choose up to {maxOptionalSelections} optional holidays.
                                    </Typography>
                                </Stack>
                                <Button
                                    variant="contained"
                                    color="warning"
                                    startIcon={<Sparkles size={20} />}
                                    onClick={() => navigate('/holidays')}
                                    sx={{
                                        fontWeight: 600,
                                        px: 3,
                                        py: 1.5,
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    Select Holidays
                                </Button>
                            </Stack>
                        </CardContent>
                    </Card>
                )}

                {/* Next Upcoming Holiday Banner */}
                {upcomingHoliday && (
                    <Card sx={{
                        mb: 3,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                        color: 'white',
                        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)'
                    }}>
                        <CardContent sx={{ p: 3 }}>
                            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems="center" spacing={3}>
                                <Stack spacing={1.5} flex={1}>
                                    <Chip
                                        icon={<Clock size={14} />}
                                        label="Next Upcoming"
                                        size="small"
                                        sx={{
                                            bgcolor: alpha('#fff', 0.2),
                                            color: 'white',
                                            width: 'fit-content',
                                            '& .MuiChip-icon': { color: 'white' }
                                        }}
                                    />
                                    <Typography variant="h5" fontWeight={600}>{upcomingHoliday.holiday_name}</Typography>
                                    <Stack direction="row" spacing={2} alignItems="center" sx={{ color: alpha('#fff', 0.9) }}>
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            <CalendarDays size={16} />
                                            <Typography variant="body2">{formatDate(upcomingHoliday.holiday_date)}</Typography>
                                        </Stack>
                                        <Chip
                                            label={getDayName(upcomingHoliday.holiday_date)}
                                            size="small"
                                            sx={{ bgcolor: alpha('#fff', 0.15), color: 'white', fontWeight: 500 }}
                                        />
                                    </Stack>
                                </Stack>
                                <Box sx={{
                                    textAlign: 'center',
                                    bgcolor: alpha('#fff', 0.15),
                                    p: 2.5,
                                    borderRadius: 2,
                                    backdropFilter: 'blur(10px)'
                                }}>
                                    <Typography variant="h3" fontWeight={700}>{getDaysUntil(upcomingHoliday.holiday_date)}</Typography>
                                    <Typography variant="caption" sx={{ color: alpha('#fff', 0.9), mt: 0.5 }}>Days Away</Typography>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                )}

                {/* Tabs Section */}
                <Card sx={{ mb: 3, borderRadius: 2 }}>
                    <Tabs
                        value={activeTab}
                        onChange={(e, newValue) => setActiveTab(newValue)}
                        variant="fullWidth"
                        sx={{
                            '& .MuiTab-root': {
                                textTransform: 'none',
                                fontWeight: 500,
                                minHeight: 56,
                                fontSize: '0.938rem'
                            },
                            '& .Mui-selected': {
                                color: isDarkMode ? '#ffffff !important' : undefined
                            }
                        }}
                    >
                        <Tab
                            value="all"
                            icon={<Calendar size={18} />}
                            iconPosition="start"
                            label={
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <span>All Holidays</span>
                                    <Chip
                                        label={allHolidaysSorted.length}
                                        size="small"
                                        sx={{
                                            bgcolor: activeTab === 'all' ? theme.palette.primary.main : alpha(theme.palette.text.primary, 0.1),
                                            color: activeTab === 'all' ? 'white' : 'text.primary',
                                            fontWeight: 600,
                                            fontSize: '0.75rem'
                                        }}
                                    />
                                </Stack>
                            }
                        />
                        <Tab
                            value="public"
                            icon={<Users size={18} />}
                            iconPosition="start"
                            label={
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <span>Public</span>
                                    <Chip
                                        label={publicHolidaysCount}
                                        size="small"
                                        sx={{
                                            bgcolor: activeTab === 'public' ? theme.palette.primary.main : alpha(theme.palette.text.primary, 0.1),
                                            color: activeTab === 'public' ? 'white' : 'text.primary',
                                            fontWeight: 600,
                                            fontSize: '0.75rem'
                                        }}
                                    />
                                </Stack>
                            }
                        />
                        <Tab
                            value="optional"
                            icon={<Sparkles size={18} />}
                            iconPosition="start"
                            label={
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <span>Optional</span>
                                    <Chip
                                        label={optionalHolidaysCount}
                                        size="small"
                                        sx={{
                                            bgcolor: activeTab === 'optional' ? theme.palette.primary.main : alpha(theme.palette.text.primary, 0.1),
                                            color: activeTab === 'optional' ? 'white' : 'text.primary',
                                            fontWeight: 600,
                                            fontSize: '0.75rem'
                                        }}
                                    />
                                </Stack>
                            }
                        />
                    </Tabs>
                </Card>

                {/* Holidays List */}
                <Card sx={{ borderRadius: 2, p: 2 }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, gap: 2 }}>
                            <CircularProgress />
                            <Typography color="text.secondary">Loading holidays...</Typography>
                        </Box>
                    ) : filteredHolidays.length === 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, gap: 2 }}>
                            <Calendar size={64} color={theme.palette.action.disabled} />
                            <Typography variant="h6" fontWeight={600} color="text.primary">No Holidays Found</Typography>
                            <Typography color="text.secondary">There are no holidays in this category yet.</Typography>
                        </Box>
                    ) : (
                        <Stack spacing={2}>
                            {filteredHolidays.map((holiday: any) => {
                                const daysUntil = getDaysUntil(holiday.holiday_date);
                                const isPast = daysUntil < 0;

                                return (
                                    <Card
                                        key={holiday.id}
                                        sx={{
                                            borderRadius: 2,
                                            bgcolor: isDarkMode ? alpha(theme.palette.background.paper, 0.6) : alpha(theme.palette.grey[50], 0.5),
                                            border: 1,
                                            borderColor: 'divider',
                                            opacity: isPast ? 0.6 : 1,
                                            transition: 'all 0.2s ease',
                                            cursor: 'pointer',
                                            '&:hover': {
                                                bgcolor: isDarkMode ? alpha(theme.palette.background.paper, 0.8) : alpha(theme.palette.grey[100], 0.8),
                                                borderColor: isDarkMode ? 'grey.700' : 'grey.300',
                                                boxShadow: 2,
                                                '& .holiday-arrow': {
                                                    transform: 'translateX(4px)'
                                                }
                                            }
                                        }}
                                    >
                                        <CardContent sx={{ p: 2.5 }}>
                                            <Stack direction="row" spacing={2} alignItems="center">
                                                {/* Date Box */}
                                                <Box sx={{
                                                    width: 80,
                                                    height: 80,
                                                    borderRadius: 2,
                                                    border: 2,
                                                    borderColor: holiday.type === 'public'
                                                        ? (isDarkMode ? 'info.dark' : 'info.light')
                                                        : (isDarkMode ? 'success.dark' : 'success.light'),
                                                    bgcolor: holiday.type === 'public'
                                                        ? (isDarkMode ? alpha(theme.palette.info.dark, 0.1) : alpha(theme.palette.info.light, 0.1))
                                                        : (isDarkMode ? alpha(theme.palette.success.dark, 0.1) : alpha(theme.palette.success.light, 0.1)),
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0
                                                }}>
                                                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                                        {getMonthName(holiday.holiday_date)}
                                                    </Typography>
                                                    <Typography variant="h4" fontWeight={700} color={holiday.type === 'public' ? 'info.main' : 'success.main'}>
                                                        {getDay(holiday.holiday_date)}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.disabled" fontWeight={500} sx={{ textTransform: 'uppercase' }}>
                                                        {getDayName(holiday.holiday_date)}
                                                    </Typography>
                                                </Box>

                                                {/* Holiday Info */}
                                                <Stack flex={1} spacing={1}>
                                                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1}>
                                                        <Typography variant="h6" fontWeight={600} color="text.primary">
                                                            {holiday.holiday_name}
                                                        </Typography>
                                                        <Chip
                                                            icon={holiday.type === 'public' ? <Users size={14} /> : <CheckCircle2 size={14} />}
                                                            label={holiday.type === 'public' ? 'Public Holiday' : 'Optional'}
                                                            size="small"
                                                            color={holiday.type === 'public' ? 'info' : 'success'}
                                                            variant="outlined"
                                                            sx={{
                                                                fontWeight: 500,
                                                                '& .MuiChip-icon': {
                                                                    color: holiday.type === 'public' ? theme.palette.info.main : theme.palette.success.main
                                                                }
                                                            }}
                                                        />
                                                    </Stack>
                                                    <Stack direction="row" spacing={2} alignItems="center">
                                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                                            <CalendarDays size={14} color={theme.palette.text.secondary} />
                                                            <Typography variant="body2" color="text.secondary">
                                                                {formatDate(holiday.holiday_date)}
                                                            </Typography>
                                                        </Stack>
                                                        {!isPast && (
                                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                                <Clock size={14} color={theme.palette.text.secondary} />
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {daysUntil === 0 ? 'Today' : `${daysUntil} day${daysUntil > 1 ? 's' : ''} away`}
                                                                </Typography>
                                                            </Stack>
                                                        )}
                                                        {isPast && (
                                                            <Typography variant="body2" color="text.disabled" fontWeight={500}>
                                                                Completed
                                                            </Typography>
                                                        )}
                                                    </Stack>
                                                </Stack>

                                                {/* Arrow */}
                                                <Box
                                                    className="holiday-arrow"
                                                    sx={{
                                                        color: 'action.disabled',
                                                        transition: 'all 0.2s ease',
                                                        display: { xs: 'none', sm: 'block' }
                                                    }}
                                                >
                                                    <ChevronRight size={20} />
                                                </Box>
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </Stack>
                    )}
                </Card>
            </Box>
        </Layout>
    );
};

export default HolidayCalendarView;
